import { Base, Component, Prop } from '@/vue-property-decorator'
import { SystemInfos } from '@/api/common/type'
import { checkPasswordStrength } from '@/utils/PasswordStrength'
import { createPngSprite } from '@/utils/business-utils/assets'
import { useI18n } from '@/i18n'
import { useMainStore } from '@/store/index'
import style from './style.module.scss'

type Props = {
  /* 密码 */
  password: string
  /* 账号 */
  account?: string
}

type ScoreType = 1 | 2 | 3 | 4

/**
 * 密码强度校验组件
 * 密码强度UI展示
 * @Date 2024.1.11
 */
@Component<PassStrengthCheck>({ name: 'PassStrengthCheck' })
export default class PassStrengthCheck extends Base<unknown, Props> {
  @Prop()
  password!: Props['password']
  @Prop()
  account?: Props['account']

  /**
   * 是否展示 密码检测是否已填
   */
  get isShowStrengthCheck() {
    const { loginPasswdStrengthDetectSwitch } = useMainStore()
      ?.systemInfos as SystemInfos
    return !!loginPasswdStrengthDetectSwitch
  }
  /**
   * 密码强度校验得分
   */
  get evaluatePasswordStrength() {
    const account = this.account
      ? this.account
      : useMainStore().userInfos?.platfromid ?? ''
    let score = checkPasswordStrength(this.password, account)
    // 用户只要有输入密码最低展示一个强度
    score = score ? score : this.password ? 1 : 0
    // score密码强度得分，范围为0到4
    return Array.from({ length: 4 }, (_, index) =>
      index < score ? (score as ScoreType) : 0
    )
  }

  /**
   * 检测密码是否包含大小写字母、数字、字符
   */
  private get detectionCode() {
    const { t } = useI18n()
    const {
      strongPasswdRequireUppercase,
      strongPasswdRequireLowercase,
      strongPasswdRequireNum,
      strongPasswdRequireSpecial
    } = useMainStore()?.systemInfos as SystemInfos
    const regex1 = /[A-Z]/
    const regex2 = /[a-z]/
    const regex3 = /\d/
    const regex4 = /[!@#$%^&*_=\+\\,\-.\/?\(\)]/
    return [
      {
        lable: t('lobby.common.formRules.uppercase'),
        satisfy: regex1.test(this.password),
        isShow: strongPasswdRequireUppercase
      },
      {
        lable: t('lobby.common.formRules.lowercase'),
        satisfy: regex2.test(this.password),
        isShow: strongPasswdRequireLowercase
      },
      {
        lable: t('lobby.common.formRules.num'),
        satisfy: regex3.test(this.password),
        isShow: strongPasswdRequireNum
      },
      {
        lable: t('lobby.common.formRules.special'),
        satisfy: regex4.test(this.password),
        isShow: strongPasswdRequireSpecial
      }
    ]
  }

  render() {
    const { isWeb } = useMainStore()
    const { t } = useI18n()
    const { strongPasswdLength, loginPasswdStrengthDetectSwitch } =
      useMainStore()?.systemInfos as SystemInfos
    let digit = ''
    if (!loginPasswdStrengthDetectSwitch) {
      digit = '6-16'
    } else if (strongPasswdLength === 16) {
      digit = '16'
    } else {
      digit = `${strongPasswdLength ?? 6}-16`
    }
    return (
      <div class={style.passStrengthCheck}>
        <div class={style.cryptographicStrength}>
          <span>{t('lobby.common.formRules.psswdstrength')}</span>
          {this.evaluatePasswordStrength.map((item) => {
            return (
              <span
                class={[style.colorLump, item ? style[`colorLump${item}`] : '']}
              />
            )
          })}
          {this.isShowStrengthCheck && (
            <span class={style.digit}>
              {digit}
              {t('lobby.common.formRules.digit')}
            </span>
          )}
        </div>
        {this.isShowStrengthCheck && (
          <div class={style.strengthCheck}>
            {this.detectionCode.map((item) => {
              return item.isShow ? (
                <div class={item.satisfy ? style.satisfy : ''}>
                  <icon-sprite
                    class={style.icon}
                    sprite={createPngSprite(
                      item.satisfy
                        ? 'sprite_main_comm_icon_pay_1'
                        : 'sprite_main_comm_icon_pay_2'
                    )}
                    scale={isWeb ? 0.28 : 0.3}
                  />
                  <span>{item.lable}</span>
                </div>
              ) : null
            })}
          </div>
        )}
      </div>
    )
  }
}
